export const SPEED_FACTORS = {
  slow: 0.75,
  normal: 1.0,
  fast: 1.5,
};

export const validateMediaUrl = (url) => {
  if (!url || typeof url !== "string") {
    return false;
  }
  const str = url.trim();
  if (!str) return false;

  const lower = str.toLowerCase();
  if (/^(?:javascript|data|vbscript):/i.test(str)) {
    return false;
  }

  if (lower.startsWith("http://") || lower.startsWith("//")) {
    return false;
  }

  if (str.startsWith("https://") || (str.startsWith("/") && !str.startsWith("//"))) {
    return true;
  }

  if (/^[a-zA-Z0-9_-]+$/.test(str)) {
    return true;
  }

  return false;
};

export const resolveAudioUrl = (urlOrDimuId) => {
  if (!validateMediaUrl(urlOrDimuId)) {
    return null;
  }
  const str = urlOrDimuId.trim();
  if (str.startsWith("https://") || str.startsWith("/")) {
    return str;
  }
  return `https://ems.dimu.org/file/${str}`;
};

export const extractTextFromBlock = (block) => {
  if (!block) return "";
  let text = "";

  if (block.type === "text" && block.text) {
    try {
      const parsed = JSON.parse(block.text);
      if (parsed && Array.isArray(parsed.blocks)) {
        text += " " + parsed.blocks.map((b) => b.text || "").join(" ");
      } else {
        text += " " + block.text;
      }
    } catch (e) {
      text += " " + block.text;
    }
  } else if (block.type === "image") {
    if (block.caption) text += " " + block.caption;
    else if (block.altText) text += " " + block.altText;
  } else if (block.type === "quiz") {
    if (block.question) text += " " + block.question;
    if (Array.isArray(block.alternatives)) {
      text += " " + block.alternatives.map((a) => a.text || "").join(" ");
    }
    if (block.explanation) text += " " + block.explanation;
  } else if (block.type === "readMore" && block.text) {
    text += " " + block.text;
  } else if (block.title) {
    text += " " + block.title;
  } else if (block.text && typeof block.text === "string") {
    text += " " + block.text;
  }

  const cleaned = text
    .replace(/SCROLL\s+FOR\s+Å\s+LESE/gi, "")
    .replace(/[↓↑→←▼▲▶◀]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned;
};

export const extractContainerText = (container) => {
  if (!container || !Array.isArray(container.blocks)) {
    return "";
  }
  const parts = [];
  container.blocks.forEach((block) => {
    const text = extractTextFromBlock(block);
    if (text) parts.push(text);
  });
  return parts.join(". ").replace(/\.\s*\./g, ".").trim();
};

export const extractSectionPlainText = (section) => {
  if (!section) return "";
  const parts = [];
  if (section.title) parts.push(section.title.trim());

  if (Array.isArray(section.containers)) {
    section.containers.forEach((container) => {
      const text = extractContainerText(container);
      if (text) parts.push(text);
    });
  }

  return parts.join("\n\n").trim();
};

export const buildStorySteps = (sections = []) => {
  if (!Array.isArray(sections)) return [];
  const steps = [];

  sections.forEach((section, sectionIndex) => {
    const containers = Array.isArray(section?.containers) ? section.containers : [];
    const hasAudio = Boolean(section?.audioTrack?.url);
    const audioUrl = section?.audioTrack?.url || null;

    if (containers.length === 0) {
      const text = section?.title || "";
      const wordCount = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
      steps.push({
        sectionIndex,
        containerIndex: 0,
        domContainerId: `container-${sectionIndex}-0`,
        domSectionId: `section-${sectionIndex}`,
        text,
        wordCount,
        durationSeconds: Math.max(5, Math.round((wordCount / 180) * 60)),
        hasAudio,
        audioUrl,
        hasVideo: false,
        isLoopingVideo: false,
      });
    } else {
      containers.forEach((container, containerIndex) => {
        const videoBlock = (container?.blocks || []).find(
          (b) => b?.type === "video" && (b?.videoUrl || b?.url)
        );
        const hasVideo = Boolean(videoBlock);
        const isLoopingVideo = Boolean(videoBlock?.loop);

        let text = extractContainerText(container);
        if (!text && !hasVideo && containerIndex === 0 && section?.title) {
          text = section.title;
        }
        const wordCount = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
        const durationSeconds = Math.max(5, Math.round((wordCount / 180) * 60));
        steps.push({
          sectionIndex,
          containerIndex,
          domContainerId: `container-${sectionIndex}-${containerIndex}`,
          domSectionId: `section-${sectionIndex}`,
          text,
          wordCount,
          durationSeconds,
          hasAudio,
          audioUrl,
          hasVideo,
          isLoopingVideo,
        });
      });
    }
  });

  return steps;
};

export const countSectionWords = (section) => {
  if (!section || !Array.isArray(section.containers)) {
    return 0;
  }

  let fullText = "";
  section.containers.forEach((container) => {
    (container?.blocks || []).forEach((block) => {
      fullText += " " + extractTextFromBlock(block);
    });
  });

  const words = fullText.trim().split(/\s+/).filter(Boolean);
  return words.length;
};

export const calculateReadingDurationSeconds = (
  section,
  { speed = "normal", wordsPerMinute = 180, minContainerSeconds = 5 } = {}
) => {
  const containers = section?.containers || [];
  const containerFloor = Math.max(1, containers.length) * minContainerSeconds;
  const wordCount = countSectionWords(section);
  const wordSeconds = (wordCount / wordsPerMinute) * 60;

  const speedFactor = SPEED_FACTORS[speed] || 1.0;
  const rawSeconds = Math.max(containerFloor, wordSeconds) / speedFactor;

  return Math.round(rawSeconds * 10) / 10;
};

export const calculateInterpolatedScrollTop = ({
  progressRatio,
  sectionTop,
  sectionScrollSpan,
}) => {
  const clamped = Math.max(0, Math.min(1, progressRatio));
  return Math.round(sectionTop + sectionScrollSpan * clamped);
};

export const evaluateAutoplayStep = ({
  sectionIndex,
  sections,
  locks,
  isAutoplayActive,
}) => {
  if (!isAutoplayActive || !Array.isArray(sections) || sectionIndex >= sections.length) {
    return { status: "COMPLETED", nextSectionIndex: null };
  }

  const section = sections[sectionIndex];
  if (!section) {
    return { status: "COMPLETED", nextSectionIndex: null };
  }

  const sectionIdMatch = section.order ?? section.id ?? sectionIndex;
  const activeLock = (locks || []).find(
    (l) => l.sectionId === sectionIdMatch || l.sectionId === sectionIndex || l.sectionId === section.order
  );

  if (activeLock) {
    return {
      status: "PAUSED_LOCKED",
      lock: activeLock,
      sectionIndex,
    };
  }

  if (section.audioTrack?.url) {
    return {
      status: "PLAYING_AUDIO",
      url: section.audioTrack.url,
      autoAdvanceOnEnd: Boolean(section.audioTrack.autoAdvanceOnEnd),
      sectionIndex,
    };
  }

  const durationSeconds = calculateReadingDurationSeconds(section);
  return {
    status: "PLAYING_TIMER",
    durationSeconds,
    sectionIndex,
  };
};

export const findBestNorwegianVoice = (voices = []) => {
  if (!Array.isArray(voices) || voices.length === 0) return null;

  const isNorwegian = (v) => {
    const lang = (v?.lang || "").replace("_", "-").toLowerCase();
    const name = (v?.name || "").toLowerCase();
    const uri = (v?.voiceURI || "").toLowerCase();
    return (
      lang.startsWith("nb") ||
      lang.startsWith("no") ||
      lang.startsWith("nn") ||
      name === "nb" ||
      name === "no" ||
      name.includes("norsk") ||
      name.includes("norwegian") ||
      name.includes("bokmål") ||
      name.includes("bokmal") ||
      name.includes("nynorsk") ||
      name.includes("pernille") ||
      name.includes("finn") ||
      name.includes("iselin") ||
      name.includes("nora") ||
      name.includes("henrik") ||
      uri.includes("norwegian") ||
      uri.includes("bokm") ||
      uri.includes("gmq/nb")
    );
  };

  const isScandinavian = (v) => {
    const lang = (v?.lang || "").replace("_", "-").toLowerCase();
    const name = (v?.name || "").toLowerCase();
    return (
      lang.startsWith("sv") ||
      lang.startsWith("da") ||
      name.includes("svensk") ||
      name.includes("dansk") ||
      name.includes("swedish") ||
      name.includes("danish")
    );
  };

  const norwegianVoices = voices.filter(isNorwegian);
  if (norwegianVoices.length > 0) {
    // Prioritize neural / natural / online voices
    const neuralVoice = norwegianVoices.find((v) => {
      const name = (v?.name || "").toLowerCase();
      return (
        name.includes("natural") ||
        name.includes("online") ||
        name.includes("google") ||
        name.includes("microsoft") ||
        name.includes("neural")
      );
    });
    if (neuralVoice) return neuralVoice;

    // Filter out espeak if another Norwegian voice exists
    const nonEspeak = norwegianVoices.find(
      (v) => !(v?.name || "").toLowerCase().includes("espeak")
    );
    if (nonEspeak) return nonEspeak;

    return norwegianVoices[0];
  }

  // Fallback to Scandinavian voice (Swedish / Danish) if no Norwegian voice exists
  const scandinavianVoices = voices.filter(isScandinavian);
  if (scandinavianVoices.length > 0) {
    const nonEspeakScan = scandinavianVoices.find(
      (v) => !(v?.name || "").toLowerCase().includes("espeak")
    );
    return nonEspeakScan || scandinavianVoices[0];
  }

  return null;
};

export const splitTextIntoChunks = (text, maxLen = 160) => {
  if (!text || typeof text !== "string") return [];
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
  const chunks = [];

  sentences.forEach((sentence) => {
    const trimmed = sentence.trim();
    if (!trimmed) return;
    if (trimmed.length <= maxLen) {
      chunks.push(trimmed);
    } else {
      const words = trimmed.split(/\s+/);
      let current = "";
      words.forEach((w) => {
        if ((current + " " + w).length <= maxLen) {
          current = current ? current + " " + w : w;
        } else {
          if (current) chunks.push(current);
          current = w;
        }
      });
      if (current) chunks.push(current);
    }
  });

  return chunks;
};
